import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { api, apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Video, Plus, Trash2, Pencil, RefreshCw, Wifi, WifiOff, Play } from "lucide-react";

const EMPTY = {
  name: "", vendor: "CP Plus", host: "", port: "80", scheme: "http", channel: "1",
  username: "", password: "", rtsp_path: "", http_snapshot_path: "", hls_url: "",
  location_note: "", enabled: true,
};

const LiveTile = ({ camera }) => {
  const videoRef = useRef(null);
  const [tick, setTick] = useState(Date.now());
  const [failed, setFailed] = useState(false);
  const token = localStorage.getItem("token");
  const snapUrl = `${API}/cameras/${camera.id}/snapshot.jpg?auth=${token}&t=${tick}`;

  useEffect(() => {
    if (camera.hls_url || !camera.enabled) return;
    const id = setInterval(() => setTick(Date.now()), 5000);
    return () => clearInterval(id);
  }, [camera.hls_url, camera.enabled]);

  useEffect(() => {
    if (!camera.hls_url) return;
    const video = videoRef.current;
    if (!video) return;
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = camera.hls_url;
      return;
    }
    if (Hls.isSupported()) {
      const hls = new Hls({ lowLatencyMode: true });
      hls.loadSource(camera.hls_url);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_e, d) => { if (d.fatal) setFailed(true); });
      return () => hls.destroy();
    }
    setFailed(true);
  }, [camera.hls_url]);

  if (!camera.enabled) {
    return <div className="aspect-video rounded-lg bg-muted flex items-center justify-center text-xs text-muted-foreground"
      data-testid={`camera-off-${camera.id}`}>Camera switched off</div>;
  }

  if (camera.hls_url && !failed) {
    return <video ref={videoRef} controls muted autoPlay playsInline
      data-testid={`camera-live-${camera.id}`}
      className="aspect-video w-full rounded-lg bg-black object-cover" />;
  }

  return (
    <div className="relative aspect-video rounded-lg bg-zinc-950 overflow-hidden">
      <img key={tick} src={snapUrl} alt={`${camera.name} snapshot`}
        data-testid={`camera-snapshot-${camera.id}`}
        onError={() => setFailed(true)} onLoad={() => setFailed(false)}
        className="h-full w-full object-cover" />
      {failed && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-zinc-950 p-4 text-center"
          data-testid={`camera-error-${camera.id}`}>
          <WifiOff size={20} className="text-red-400" />
          <p className="text-xs text-zinc-300">Feed unavailable</p>
          <p className="text-[11px] text-zinc-500 leading-snug">
            {camera.last_error || "The server could not reach this camera. Check the public host, port forwarding and credentials."}
          </p>
        </div>
      )}
      <span className="absolute top-2 left-2 rounded-md bg-black/70 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
        {failed ? "Offline" : "Live · refreshes every 5s"}
      </span>
    </div>
  );
};

export const CamerasPanel = ({ parcelId, isAdmin }) => {
  const [list, setList] = useState([]);
  const [vendors, setVendors] = useState(["CP Plus", "Dahua", "Hikvision", "Other"]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState("");

  const load = () => api.get(`/parcels/${parcelId}/cameras`)
    .then(({ data }) => setList(data)).catch(() => {});

  useEffect(() => {
    load();
    api.get("/camera-vendors").then(({ data }) => setVendors(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parcelId]);

  const openAdd = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({
      ...EMPTY, ...c, port: String(c.port ?? 80), channel: String(c.channel ?? 1),
      password: "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        ...form, port: parseInt(form.port, 10) || 80,
        channel: parseInt(form.channel, 10) || 1,
        password: form.password || null,
      };
      if (editId) await api.put(`/cameras/${editId}`, body);
      else await api.post(`/parcels/${parcelId}/cameras`, body);
      toast.success(editId ? "Camera updated" : "Camera added");
      setOpen(false); setEditId(null); setForm(EMPTY); load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/cameras/${id}`);
      toast.success("Camera removed");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const test = async (id) => {
    setTesting(id);
    try {
      const { data } = await api.post(`/cameras/${id}/test`);
      if (data.ok) toast.success("Camera reachable", { description: data.detail });
      else toast.error("Camera not reachable", { description: data.detail });
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setTesting(""); }
  };

  return (
    <div data-testid="cameras-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="label-tiny flex items-center gap-2">
            <Video size={14} className="text-primary" /> Live monitoring
          </p>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
            Optional CCTV feeds for this property. REAM shows an HLS stream when your NVR provides
            one, otherwise it refreshes a live snapshot every 5 seconds. The camera must be
            reachable over the internet (DDNS or port forwarding) — credentials are stored encrypted.
          </p>
        </div>
        {isAdmin && (
          <Button onClick={openAdd} data-testid="add-camera-button" className="rounded-lg">
            <Plus size={15} className="mr-2" /> Add camera
          </Button>
        )}
      </div>

      {list.length === 0 ? (
        <div className="mt-6 surface p-12 text-center" data-testid="cameras-empty">
          <Video size={22} className="mx-auto text-muted-foreground" />
          <p className="mt-3 font-display text-xl font-bold tracking-tight">No cameras yet</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Add a CP Plus, Dahua or Hikvision camera or NVR channel to watch this site live.
          </p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {list.map((c) => (
            <div key={c.id} className="surface p-4" data-testid={`camera-card-${c.id}`}>
              <LiveTile camera={c} />
              <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{c.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {c.vendor} · channel {c.channel} · {c.host}:{c.port}
                    {c.location_note ? ` · ${c.location_note}` : ""}
                  </p>
                  <p className="mt-1 text-xs flex items-center gap-1.5">
                    {c.last_error ? (
                      <span className="text-red-600 flex items-center gap-1.5">
                        <WifiOff size={12} /> {c.last_error}
                      </span>
                    ) : c.last_ok_at ? (
                      <span className="text-emerald-600 flex items-center gap-1.5">
                        <Wifi size={12} /> Last frame {c.last_ok_at.slice(0, 16).replace("T", " ")}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Not tested yet</span>
                    )}
                  </p>
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" data-testid={`test-camera-${c.id}`}
                      disabled={testing === c.id} onClick={() => test(c.id)}>
                      {testing === c.id
                        ? <RefreshCw size={13} className="animate-spin" />
                        : <Play size={13} />}
                    </Button>
                    <Button variant="outline" size="sm" data-testid={`edit-camera-${c.id}`}
                      onClick={() => openEdit(c)}>
                      <Pencil size={13} />
                    </Button>
                    <Button variant="outline" size="sm" data-testid={`delete-camera-${c.id}`}
                      onClick={() => remove(c.id)}>
                      <Trash2 size={13} className="text-destructive" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display font-bold tracking-tight">
              {editId ? "Edit camera" : "Add camera"}
            </DialogTitle>
            <DialogDescription>
              Use the public hostname or DDNS name of the camera or NVR — not its LAN address.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="camera-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="label-tiny" htmlFor="cam-name">Camera name</Label>
                <Input id="cam-name" required value={form.name} data-testid="camera-name-input"
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Main gate" className="mt-1.5" />
              </div>
              <div>
                <Label className="label-tiny">Vendor</Label>
                <Select value={form.vendor} onValueChange={(v) => setForm({ ...form, vendor: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="camera-vendor-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-tiny" htmlFor="cam-channel">Channel</Label>
                <Input id="cam-channel" type="number" min="1" value={form.channel}
                  data-testid="camera-channel-input"
                  onChange={(e) => setForm({ ...form, channel: e.target.value })} className="mt-1.5" />
              </div>
              <div className="col-span-2">
                <Label className="label-tiny" htmlFor="cam-host">Public host / DDNS</Label>
                <Input id="cam-host" required value={form.host} data-testid="camera-host-input"
                  onChange={(e) => setForm({ ...form, host: e.target.value })}
                  placeholder="site1.cpplusddns.com" className="mt-1.5 font-mono" />
              </div>
              <div>
                <Label className="label-tiny">Protocol</Label>
                <Select value={form.scheme} onValueChange={(v) => setForm({ ...form, scheme: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="camera-scheme-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="http">http</SelectItem>
                    <SelectItem value="https">https</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-tiny" htmlFor="cam-port">HTTP port</Label>
                <Input id="cam-port" type="number" min="1" value={form.port}
                  data-testid="camera-port-input"
                  onChange={(e) => setForm({ ...form, port: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className="label-tiny" htmlFor="cam-user">Username</Label>
                <Input id="cam-user" value={form.username} data-testid="camera-username-input"
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  placeholder="viewer" className="mt-1.5" />
              </div>
              <div>
                <Label className="label-tiny" htmlFor="cam-pass">
                  Password {editId && form.has_password ? "(leave blank to keep)" : ""}
                </Label>
                <Input id="cam-pass" type="password" value={form.password}
                  data-testid="camera-password-input"
                  onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" />
              </div>
              <div className="col-span-2">
                <Label className="label-tiny" htmlFor="cam-hls">HLS stream URL (optional)</Label>
                <Input id="cam-hls" value={form.hls_url} data-testid="camera-hls-input"
                  onChange={(e) => setForm({ ...form, hls_url: e.target.value })}
                  placeholder="https://media.example.com/hls/gate/index.m3u8" className="mt-1.5 font-mono" />
                <p className="mt-1.5 text-xs text-muted-foreground">
                  If your NVR or media gateway publishes HLS, paste it here for true live video.
                  Leave blank to use snapshot refresh.
                </p>
              </div>
              <div className="col-span-2">
                <Label className="label-tiny" htmlFor="cam-snap">Snapshot path (optional)</Label>
                <Input id="cam-snap" value={form.http_snapshot_path} data-testid="camera-snapshot-path-input"
                  onChange={(e) => setForm({ ...form, http_snapshot_path: e.target.value })}
                  placeholder="/cgi-bin/snapshot.cgi?channel=1" className="mt-1.5 font-mono" />
              </div>
              <div className="col-span-2">
                <Label className="label-tiny" htmlFor="cam-note">Location note</Label>
                <Input id="cam-note" value={form.location_note} data-testid="camera-note-input"
                  onChange={(e) => setForm({ ...form, location_note: e.target.value })}
                  placeholder="North boundary, facing the approach road" className="mt-1.5" />
              </div>
              <div className="col-span-2 flex items-center gap-3 pt-1">
                <Switch id="cam-enabled" checked={!!form.enabled} data-testid="camera-enabled-toggle"
                  onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
                <Label htmlFor="cam-enabled" className="text-sm">Camera is active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={busy} data-testid="save-camera-button">
                {busy ? "Saving…" : editId ? "Save changes" : "Add camera"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
